import { BETA_FEATURES, NAV_ITEMS } from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import * as dataSelectors from '@/data/data-selectors';
import { isBetaFeatureEnabled } from '@/data/data-selectors';
import * as filterSelectors from '@/filter/filter-selectors';
import * as listSelectors from '@/list/list-selectors';
import { CustomMapControls } from '@/map/custom-map-controls/custom-map-controls';
import { addCustomControlMounts } from '@/map/custom-map-controls/map-ctrl-utils';
import { generateGeoJson } from '@/map/geojson-gen';
import * as mapActions from '@/map/map-actions';
import * as constants from '@/map/map-constants';
import { MapboxInstanceMgr } from '@/map/map-instance';
import * as mapSelectors from '@/map/map-selectors';
import { pulsingDotMgr } from '@/map/pulsing-dot/pulsing-dot-mgr';
import { MapSkeleton } from '@/placeholders';
import { smartDecodeURIComponent } from '@/utils/uri-utils';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  InsertMapHere,
  MapContainerLayout,
  MapContainerWrapper,
  TagWrapper,
} from './map-container-styles';
import { MapFilterParamTags } from './map-filter-param-tags';
import { MapInfoModal } from './map-info-modal';
import { registerMapResizer } from './map-viewer-utils';

/**
 * This component manages the map filters and renders the map in the map view
 */
export function MapContainer() {
  const cityStats = useSelector(dataSelectors.stats);
  const city = cityStats.name;
  const filteredPropertyList = useSelector(
    filterSelectors.filteredPropertyList
  );

  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const featuredDeckardIdEncoded = queryParams.get('featured');

  const featuredDeckardId = useMemo(() => {
    return featuredDeckardIdEncoded
      ? smartDecodeURIComponent(featuredDeckardIdEncoded)
      : null;
  }, [featuredDeckardIdEncoded]);

  const featuredProperty = useSelector((state) => {
    return featuredDeckardId
      ? dataSelectors.getPropertyData(state, featuredDeckardId)
      : null;
  });

  const magManagerInstance = MapboxInstanceMgr.getInstance();

  const dispatch = useDispatch();

  const [customControlNodes, setCustomControlNodes] = useState<
    HTMLElement[] | null
  >(null);
  const [showMapInfoModal, setShowMapInfoModal] = useState(false);

  const mapFilterParams = useSelector((state) =>
    mapSelectors.mapFilterParams(state)
  );
  const shapeFile = useSelector((state) => mapSelectors.shapeFile(state));
  const zoningFiles = useSelector((state) => mapSelectors.zoningFiles(state));
  const filterSectionTagsMap = useSelector((state) =>
    // @ts-expect-error filterSectionTagsMap is not typed
    filterSelectors.filterSectionTagsMap(state)
  );

  const disableClustering = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.disableMapClustering)
  );
  const platformIcons = useSelector(dataSelectors.platformIcons);
  const displayUnlicensedPropertiesSeparately = useSelector(
    listSelectors.isLicensingEnabled
  );

  const isMapLoaded = useCallback(
    () => Boolean(customControlNodes),
    [customControlNodes]
  );

  const onDotClick = useCallback((propertyDeckardID) => {
    dispatch(
      dashboardActions.openPropertyByDeckardId([
        NAV_ITEMS.home,
        propertyDeckardID,
      ])
    );
  }, []);

  useEffect(() => {
    pulsingDotMgr.stopAllDots();

    // @ts-expect-error magManagerInstance is not typed
    magManagerInstance.init({
      cityInfo: cityStats,
      platformIcons,
      displayUnlicensedPropertiesSeparately,
      onDotClick,
      disableClustering,
      addCustomControlMountsCB: () =>
        addCustomControlMounts(setCustomControlNodes),
    });

    return () => {
      // @ts-expect-error magManagerInstance is not typed
      magManagerInstance.reset();
    };
  }, [
    disableClustering,
    displayUnlicensedPropertiesSeparately,
    magManagerInstance,
    cityStats,
    platformIcons,
    onDotClick,
  ]);

  useEffect(() => {
    if (filteredPropertyList && isMapLoaded()) {
      const initialGeoJson = generateGeoJson(filteredPropertyList);
      // @ts-expect-error magManagerInstance is not typed
      magManagerInstance.drawProperties(initialGeoJson);
    }
  }, [filteredPropertyList, isMapLoaded, magManagerInstance]);

  useEffect(() => {
    if (shapeFile && isMapLoaded()) {
      // @ts-expect-error magManagerInstance is not typed
      magManagerInstance.drawBoundary(shapeFile);
    }
  }, [shapeFile, isMapLoaded, magManagerInstance]);

  useEffect(() => {
    if (zoningFiles && isMapLoaded()) {
      // @ts-expect-error magManagerInstance is not typed
      magManagerInstance.drawZoning(zoningFiles);
    }
  }, [zoningFiles, isMapLoaded, magManagerInstance]);

  useEffect(() => {
    if (!isMapLoaded() || !featuredDeckardId || !featuredProperty) {
      // Optional: Add cleanup for any existing dot if the new ID/property is invalid
      return;
    }
    const effectFunc = pulsingDotMgr.locateOnMapWithInfoPopup(
      featuredProperty,
      featuredDeckardId,
      platformIcons,
      history
    );
    effectFunc();
  }, [
    isMapLoaded,
    featuredDeckardId,
    featuredProperty,
    platformIcons,
    history,
    dispatch,
  ]);

  // FIXME: There is a bug here that causes the custom controls not to appear when switchingcities
  // useEffect(mapBooterRebooter, [props.shapeFile, props.zoningFiles]);

  useEffect(() => {
    registerMapResizer(() => {
      // @ts-expect-error magManagerInstance is not typed
      if (magManagerInstance?.mapInstance?.loaded())
        // @ts-expect-error magManagerInstance is not typed
        magManagerInstance.mapInstance?.resize();
    });
    // @ts-expect-error magManagerInstance is not typed
  }, [magManagerInstance?.mapInstance]);

  /**
   * monitor the filter, mapFilterParams
   * when users operate on the map fitler, filtered property list will be changed, obviously
   * then, we update the property list on map
   */
  useEffect(() => {
    if (!isEmpty(pulsingDotMgr.pulsingDots)) pulsingDotMgr.reset();
    if (magManagerInstance) {
      // @ts-expect-error magManagerInstance is not typed
      magManagerInstance.updatePropertyData(
        generateGeoJson(filteredPropertyList)
      );
    }
  }, [filteredPropertyList, magManagerInstance, mapFilterParams]);

  const handleCheckboxOnChange = (
    groupName: string,
    option: string,
    checked: boolean
  ) => {
    dispatch(
      mapActions.setSingleFilterParam({ groupName, option, value: checked })
    );
  };

  return (
    <MapContainerWrapper>
      <MapSkeleton show={!customControlNodes} />
      <MapContainerLayout>
        <TagWrapper>
          <MapFilterParamTags
            mapFilterParams={mapFilterParams}
            handleCheckboxOnChange={handleCheckboxOnChange}
            filterSectionTagsMap={filterSectionTagsMap}
          />
        </TagWrapper>
        <InsertMapHere
          data-cy="map-container"
          id={constants.MAP_CONTAINER_ID}
        />
      </MapContainerLayout>
      {customControlNodes && (
        <CustomMapControls
          city={cityStats}
          customControlNodes={customControlNodes}
        />
      )}
      {showMapInfoModal && (
        <MapInfoModal
          info={{ city: city }}
          closeModal={() => setShowMapInfoModal(false)}
        />
      )}
    </MapContainerWrapper>
  );
}
